import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Avatar,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import {
  Login,
  Logout,
  AccessTime,
  CheckCircle,
  Person,
  Schedule,
  Business,
  Home,
  Computer,
  LocationOn,
} from "@mui/icons-material";
import axios from "axios";
import { base_url } from "../../config/config";
import dayjs from "dayjs";
import { tokens } from "../../theme";

const WorkingCheckIn = () => {
  const { user } = useSelector((state) => state.user);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const [todayStatus, setTodayStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [location, setLocation] = useState("office");
  const [notes, setNotes] = useState("");
  const navigate = useNavigate();

  // Check authentication and fetch data
  useEffect(() => {
    console.log("user", user);
    if (!user) {
      navigate("/signin");
      return;
    }
    fetchTodayStatus();
  }, [user, navigate]);

  const fetchTodayStatus = async () => {
    if (!user) return;

    try {
      console.log("Fetching attendance for user:", user.name || user.email);
      const response = await axios.get(`${base_url}/v1/getDailyAttendance`);
      const today = dayjs().format("YYYY-MM-DD");
      const userName = user.name || user.email;

      console.log("Looking for user:", userName, "on date:", today);
      console.log("Available records:", response.data.data?.slice(0, 3)); // Log first 3 records for debugging

      // Find today's record for this user (match by name or email)
      const todayRecord = response.data.data?.find((record) => {
        const recordUser = record.user;
        const recordDate = record.Date;
        const recordEmail = record.email;
        return (
          (recordUser === user.name ||
            recordUser === user.email ||
            (recordEmail && recordEmail === user.email)) &&
          recordDate === today
        );
      });

      console.log("Found today record:", todayRecord);

      if (todayRecord) {
        setTodayStatus({
          checkInTime: todayRecord.StartTime,
          checkOutTime: todayRecord.endTime,
          date: todayRecord.Date,
          workingStatus: todayRecord.endTime ? "completed" : "working",
          _id: todayRecord._id,
        });
      } else {
        setTodayStatus({ workingStatus: "absent" });
      }
      setError("");
    } catch (error) {
      console.error("Error fetching today status:", error);
      setError("Fehler beim Laden des heutigen Status");
      setTodayStatus({ workingStatus: "absent" });
    }
  };

  const handleCheckIn = async () => {
    if (!user) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      console.log("Attempting check-in for user:", user.name || user.email);

      const response = await axios.post(`${base_url}/v1/DailyAttendance`, {
        user: user.name || user.email,
        Date: dayjs().format("YYYY-MM-DD"),
        startTime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        fromlocation: "cnc-dashboard",
      });

      console.log("Check-in response:", response.data);

      if (response.status === 201) {
        setSuccess("Erfolgreich eingecheckt!");
        setNotes("");
        fetchTodayStatus();
      } else {
        setError("Check-in fehlgeschlagen");
      }
    } catch (error) {
      console.error("Check-in error:", error);
      if (error.response?.status === 201) {
        // Sometimes 201 is treated as error by axios
        setSuccess("Erfolgreich eingecheckt!");
        setNotes("");
        fetchTodayStatus();
      } else {
        setError(error.response?.data?.message || "Check-in fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!user || !todayStatus._id) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      console.log("Attempting check-out for record ID:", todayStatus._id);

      const response = await axios.post(`${base_url}/v1/updateAttendance`, {
        _id: todayStatus._id,
        checkoutTime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      });

      console.log("Check-out response:", response.data);

      if (response.status === 200) {
        setSuccess("Erfolgreich ausgecheckt!");
        setNotes("");
        fetchTodayStatus();
      } else {
        setError("Check-out fehlgeschlagen");
      }
    } catch (error) {
      console.error("Check-out error:", error);
      setError(error.response?.data?.message || "Check-out fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const getLocationIcon = (location) => {
    switch (location) {
      case "office":
        return <Business />;
      case "remote":
        return <Home />;
      case "client_site":
        return <LocationOn />;
      default:
        return <Computer />;
    }
  };

  const getLocationLabel = (location) => {
    switch (location) {
      case "office":
        return "Büro";
      case "remote":
        return "Home Office";
      case "client_site":
        return "Kundenstandort";
      default:
        return "Sonstiges";
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    // Extract HH:MM from HH:MM:SS format if seconds are present
    if (timeString.includes(":")) {
      const parts = timeString.split(":");
      return `${parts[0]}:${parts[1]}`;
    }
    return timeString;
  };

  const calculateWorkingHours = () => {
    if (!todayStatus.checkInTime || !todayStatus.checkOutTime) return null;

    try {
      const checkIn = dayjs(`${todayStatus.date} ${todayStatus.checkInTime}`);
      const checkOut = dayjs(`${todayStatus.date} ${todayStatus.checkOutTime}`);

      if (!checkIn.isValid() || !checkOut.isValid()) return null;

      const diffMinutes = checkOut.diff(checkIn, "minute");
      let workingMinutes = diffMinutes;

      // Only subtract break if working time is more than 45 minutes
      if (diffMinutes > 45) {
        workingMinutes = diffMinutes - 45;
      }

      const hours = Math.floor(workingMinutes / 60);
      const minutes = Math.round(workingMinutes % 60);

      return `${hours}h ${minutes}m`;
    } catch (error) {
      console.error("Error calculating working hours:", error);
      return null;
    }
  };

  const canCheckIn =
    !todayStatus.checkInTime || todayStatus.workingStatus === "absent";
  const canCheckOut = todayStatus.checkInTime && !todayStatus.checkOutTime;

  if (!user) {
    return (
      <Box m="20px">
        <Alert severity="warning" sx={{ mb: 2 }}>
          Bitte melden Sie sich an, um auf diese Seite zuzugreifen.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "10px",
          background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
          color: "white",
          mb: 3,
          boxShadow: isDark
            ? "0 8px 20px rgba(0, 0, 0, 0.25)"
            : "0 8px 20px rgba(37, 99, 235, 0.2)",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                Willkommen, {user.name || user.email}
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                Check-in/Check-out System
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {dayjs().format("dddd, DD. MMMM YYYY")}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Box
              sx={{
                minWidth: 140,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "rgba(255,255,255,0.15)",
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                HEUTIGER STATUS
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {todayStatus?.workingStatus || "-"}
              </Typography>
            </Box>
            <Box
              sx={{
                minWidth: 140,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "rgba(255,255,255,0.15)",
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                ARBEITSZEIT
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                {calculateWorkingHours() || "-"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Status Overview */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              mb: 3,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? colors.primary[500] : colors.grey[800]}`,
              boxShadow: isDark
                ? "0 6px 16px rgba(0, 0, 0, 0.25)"
                : "0 6px 16px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom color={colors.grey[100]}>
                <CheckCircle sx={{ mr: 1, verticalAlign: "middle" }} />
                Heutiger Status
              </Typography>

              {todayStatus && Object.keys(todayStatus).length > 0 ? (
                <Box>
                  <Grid container spacing={2}>
                    {todayStatus.checkInTime && (
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 2,
                            backgroundColor: isDark ? "#1b5e20" : "#c8e6c9",
                            borderRadius: 1,
                          }}
                        >
                          <Login
                            sx={{
                              fontSize: 40,
                              color: isDark ? "#66bb6a" : "#2e7d32",
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              color: isDark ? "#a5d6a7" : "#1b5e20",
                            }}
                          >
                            {formatTime(todayStatus.checkInTime)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark
                                ? colors.grey[300]
                                : colors.grey[600],
                            }}
                          >
                            Check-in Zeit
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {todayStatus.checkOutTime && (
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 2,
                            backgroundColor: isDark ? "#b71c1c" : "#ffcdd2",
                            borderRadius: 1,
                          }}
                        >
                          <Logout
                            sx={{
                              fontSize: 40,
                              color: isDark ? "#ef5350" : "#c62828",
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              color: isDark ? "#ef9a9a" : "#b71c1c",
                            }}
                          >
                            {formatTime(todayStatus.checkOutTime)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark
                                ? colors.grey[300]
                                : colors.grey[600],
                            }}
                          >
                            Check-out Zeit
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {todayStatus.checkInTime && todayStatus.checkOutTime && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            textAlign: "center",
                            p: 2,
                            backgroundColor: isDark ? "#1b5e20" : "#c8e6c9",
                            borderRadius: 1,
                          }}
                        >
                          <Schedule
                            sx={{
                              fontSize: 40,
                              color: isDark ? "#66bb6a" : "#2e7d32",
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: "bold",
                              color: isDark ? "#a5d6a7" : "#1b5e20",
                            }}
                          >
                            {calculateWorkingHours()}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark
                                ? colors.grey[300]
                                : colors.grey[600],
                            }}
                          >
                            Arbeitszeit (abzgl. 45min Pause)
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ) : (
                <Typography
                  sx={{
                    textAlign: "center",
                    py: 2,
                    color: isDark ? colors.grey[300] : colors.grey[500],
                  }}
                >
                  Noch nicht eingecheckt heute
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Check-in/Check-out Form */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? colors.primary[500] : colors.grey[800]}`,
              boxShadow: isDark
                ? "0 6px 16px rgba(0, 0, 0, 0.25)"
                : "0 6px 16px rgba(0, 0, 0, 0.08)",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom color={colors.grey[100]}>
                <AccessTime sx={{ mr: 1, verticalAlign: "middle" }} />
                Aktionen
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              {canCheckIn && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Arbeitsort</InputLabel>
                    <Select
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      label="Arbeitsort"
                    >
                      <MenuItem value="office">
                        <Box display="flex" alignItems="center">
                          <Business sx={{ mr: 1 }} />
                          Büro
                        </Box>
                      </MenuItem>
                      <MenuItem value="remote">
                        <Box display="flex" alignItems="center">
                          <Home sx={{ mr: 1 }} />
                          Home Office
                        </Box>
                      </MenuItem>
                      <MenuItem value="client_site">
                        <Box display="flex" alignItems="center">
                          <LocationOn sx={{ mr: 1 }} />
                          Kundenstandort
                        </Box>
                      </MenuItem>
                      <MenuItem value="other">
                        <Box display="flex" alignItems="center">
                          <Computer sx={{ mr: 1 }} />
                          Sonstiges
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Notizen (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleCheckIn}
                    disabled={loading}
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <Login />
                    }
                    sx={{
                      p: 1.5,
                      fontSize: "16px",
                      fontWeight: "bold",
                      backgroundColor: "#2563eb",
                      "&:hover": { backgroundColor: "#1d4ed8" },
                    }}
                  >
                    {loading ? "Check-in..." : "Check In"}
                  </Button>
                </Box>
              )}

              {canCheckOut && (
                <Box>
                  <TextField
                    fullWidth
                    label="Abschließende Notizen (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleCheckOut}
                    disabled={loading}
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <Logout />
                    }
                    sx={{
                      p: 1.5,
                      fontSize: "16px",
                      fontWeight: "bold",
                      backgroundColor: colors.redAccent[700],
                      "&:hover": { backgroundColor: colors.redAccent[800] },
                    }}
                  >
                    {loading ? "Check-out..." : "Check Out"}
                  </Button>
                </Box>
              )}

              {!canCheckIn && !canCheckOut && todayStatus.checkOutTime && (
                <Box sx={{ textAlign: "center" }}>
                  <CheckCircle
                    sx={{ fontSize: 60, color: colors.blueAccent[400], mb: 1 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ mb: 1, color: colors.blueAccent[300] }}
                  >
                    Arbeitstag abgeschlossen
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                    Check-out um {formatTime(todayStatus.checkOutTime)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                    Arbeitszeit: {calculateWorkingHours()}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mt: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
        {/* <Button
          variant="contained"
          onClick={() => navigate("/dashboard")}
          sx={{
            backgroundColor: "#2563eb",
            color: "white",
            "&:hover": {
              backgroundColor: "#1d4ed8",
            },
          }}
        >
          Dashboard
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate("/advanced-management-log")}
          sx={{
            backgroundColor: "#2563eb",
            color: "white",
            "&:hover": {
              backgroundColor: "#1d4ed8",
            },
          }}
        >
          Management Log
        </Button> */}
        <Button
          variant="outlined"
          onClick={() =>
            navigate("/TaskLogger", {
              state: { employeeName: user.name || user.email },
            })
          }
          sx={{
            color: "#2563eb",
            "&:hover": {
              backgroundColor: "rgba(37, 99, 235, 0.1)",
            },
          }}
        >
          Task Logger
        </Button>
      </Box>
    </Box>
  );
};

export default WorkingCheckIn;
